import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction, useEffect, useMemo, useState } from 'react'

import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

import {
  buildDisplayedFilters,
  isDefaultValue,
  mergeFilterUpdates,
  toEnterpriseFormat,
  updateVisibleFilters,
} from './filterConversions'
import CarrierNameFilter from './filters/CarrierNameFilter'
import HsaEligibleFilter from './filters/HsaEligibleFilter'
import MetalTierFilter from './filters/MetalTierFilter'
import PlanTypeFilter from './filters/PlanTypeFilter'
import {
  FipsCountyCodeFilter,
  HiosIdFilter,
  MedPlanIdFilter,
  MemberIdFilter,
  ZipCodeFilter,
} from './filters/validatedFilterFactory'
import { type MedicalPlanFilters } from './hooks/useMedicalPlanFilters'

export type MedicalPlanFilterGroupType = 'Geographic' | 'Identifiers' | 'Associations' | 'Options' | 'All Filters'

type SingleSelectFilterComponents = Record<string, FC<SingleSelectedValueFilter>>
type MultiSelectFilterComponents = Record<string, FC<MultipleSelectedValuesFilter>>

export const medicalPlanSingleSelectFilters: SingleSelectFilterComponents = {
  zipCode: ZipCodeFilter,
  fipsCountyCode: FipsCountyCodeFilter,
  memberId: MemberIdFilter,
  hiosId: HiosIdFilter,
  medicalPlanId: MedPlanIdFilter,
  hsaEligible: HsaEligibleFilter,
}

export const medicalPlanMultiSelectFilters: MultiSelectFilterComponents = {
  carrierNames: CarrierNameFilter,
  metalTiers: MetalTierFilter,
  planTypes: PlanTypeFilter,
}

// Use a single group for all filters to create a flat, alphabetized list
const ALL_FILTERS_GROUP = 'All Filters'

// Filter configurations are defined in alphabetical order by label
// This ensures consistent display order without runtime sorting
export const filterConfigurations: FilterConfigurations<
  MedicalPlanFilters,
  MedicalPlanFilterGroupType
> = {
  carrierNames: {
    key: 'carrierNames',
    label: 'Carrier',
    group: ALL_FILTERS_GROUP,
    isMultiValue: true,
  },
  fipsCountyCode: {
    key: 'fipsCountyCode',
    label: 'FIPS County Code',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
  },
  hiosId: {
    key: 'hiosId',
    label: 'HIOS ID',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
  },
  hsaEligible: {
    key: 'hsaEligible',
    label: 'HSA Eligible',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
  },
  memberId: {
    key: 'memberId',
    label: 'Member ID',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
  },
  medicalPlanId: {
    key: 'medicalPlanId',
    label: 'Med Plan ID',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
    tooltip: 'Medical Plan ID, this is the primary key for insurance_medical_plans',
  },
  metalTiers: {
    key: 'metalTiers',
    label: 'Metal Tier',
    group: ALL_FILTERS_GROUP,
    isMultiValue: true,
  },
  planTypes: {
    key: 'planTypes',
    label: 'Plan Type',
    group: ALL_FILTERS_GROUP,
    isMultiValue: true,
  },
  zipCode: {
    key: 'zipCode',
    label: 'Zip Code',
    group: ALL_FILTERS_GROUP,
    isMultiValue: false,
    tooltip: 'It is better to filter by FIPS code. Medical plans may be available in only some parts of a zip code.',
  },
} as FilterConfigurations<MedicalPlanFilters, MedicalPlanFilterGroupType>

export const medicalPlanDefaultPinnedOptionsByFilterValue = {
  carrierNames: [],
  metalTiers: [],
  planTypes: [],
}

export const getMedicalPlanFiltersConfigurationSortValue = (
  _filterConfiguration: ComboboxFilterGroup<string>
): number => {
  // Return 0 for all groups so they appear together, then sort alphabetically
  return 0
}

type MedicalPlanFiltersViewProps = {
  filters: MedicalPlanFilters
  setFilters: Dispatch<SetStateAction<MedicalPlanFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const MedicalPlanFiltersView = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: MedicalPlanFiltersViewProps) => {
  // Track which filters are visible (active) independently of URL
  // This includes filters that have been added but don't have values yet
  const [visibleFilters, setVisibleFilters] = useState<Set<string>>(new Set())

  // Update visible filters when filters from URL change
  // If a filter is in the URL, it should be visible
  useEffect(() => {
    setVisibleFilters((prev) => {
      const next = new Set<string>()
      Object.keys(filterConfigurations).forEach((filterKey) => {
        const filterValue = filters[filterKey as keyof MedicalPlanFilters]
        // If filter has a non-default value in URL, it should be visible
        // OR if it was already visible (preserves newly added filters with empty values)
        if (!isDefaultValue(filterKey, filterValue) || prev.has(filterKey)) {
          next.add(filterKey)
        }
      })
      return next
    })
  }, [filters])

  // Build displayed filters using utility function
  const displayedFilters = useMemo(
    () => buildDisplayedFilters(filters, visibleFilters, filterConfigurations),
    [filters, visibleFilters]
  )

  // Convert to EnterpriseFiltersView format (booleans to strings)
  const convertedFilters = useMemo(
    () => toEnterpriseFormat(displayedFilters) as MedicalPlanFilters & { hsaEligible?: string },
    [displayedFilters]
  )

  // Handle filter updates from EnterpriseFiltersView
  const handleSetFilters = (newFilters: SetStateAction<MedicalPlanFilters & { hsaEligible?: string }>) => {
    const updated = typeof newFilters === 'function' ? newFilters(convertedFilters) : newFilters

    // Update visible filters set
    setVisibleFilters((prev) =>
      updateVisibleFilters(
        convertedFilters as Record<string, unknown>,
        updated as Record<string, unknown>,
        prev,
        filterConfigurations
      )
    )

    // Merge updated filters with existing filters
    const merged = mergeFilterUpdates(
      filters,
      convertedFilters as Record<string, unknown>,
      updated as Record<string, unknown>,
      filterConfigurations
    )

    setFilters(merged)
  }

  return (
    <EnterpriseFiltersView
      filters={convertedFilters as Record<string, unknown>}
      setFilters={handleSetFilters as Dispatch<SetStateAction<Record<string, unknown>>>}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={medicalPlanSingleSelectFilters}
      multiSelectFilters={medicalPlanMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={medicalPlanDefaultPinnedOptionsByFilterValue}
      getFiltersConfigurationSortValue={getMedicalPlanFiltersConfigurationSortValue}
      showFilterBadges={false}
    />
  )
}

export default MedicalPlanFiltersView
